import React, { useState } from 'react';
import { Page, useRegistryGiftCountsTelemetry } from '@apps/registry/common/components/RegistryGiftCounts/telemetry';
import {
  StyledWrapper,
  StyledCardWrapper,
  StyledCardContent,
  StyledDropdownContent,
  styles,
  StyledToggleButton,
  ExpandButtonContainer,
  StyledDivider
} from './RegistryGiftCountsShop.styles';
import { ReactComponent as GiftIcon } from './assets/gift.svg';
import { Box, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useRegistryGiftCounts } from '@apps/registry/common/components/RegistryGiftCounts/RegistryGiftCounts.controller';
import { PriceRange, Tooltip } from '@apps/registry/common/components/RegistryGiftCounts/components';
import { LoadingResults } from './components/LoadingResults';
import { ReactComponent as Carrot } from './assets/carrot.svg';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { GiftCountsSkeleton, EditGuestCount, CountsSummary } from './components';
import { ZERO_STATE_PRICE_RANGES } from '../../../RegistryGiftCounts/constants';

interface RegistryGiftCountsShopProps {
  eventId: string;
  page: Page;
  eventHandle: string;
}

export const RegistryGiftCountsShop = ({ eventId, page, eventHandle }: RegistryGiftCountsShopProps) => {
  const {
    getResultsLoading,
    initialDataLoading,
    formik,
    handleOnEditClick,
    isEditing,
    getPriceRangeTitleAndLink,
    completionCountTitle,
    completionPercent,
    formattedCompletionPercent,
    priceRanges,
    targetGiftCount,
    currentGiftCount,
    isInZeroState
  } = useRegistryGiftCounts({ eventId, page, eventHandle });
  const { t } = useTranslation('sharedComponents');
  const { registryGuide, howManyGuests, countsByPrice } = t('registryGiftCounts');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { registryCheatSheetExpanded } = useRegistryGiftCountsTelemetry();

  const handleOnClick = () => {
    if (!isOpen && priceRanges) {
      const currentCounts = priceRanges.reduce((acc: Record<string, number>, range) => {
        const result = getPriceRangeTitleAndLink(range);
        acc[result.telemetryLabel] = result.currentCount;
        return acc;
      }, {});
      if (targetGiftCount && currentGiftCount) {
        registryCheatSheetExpanded({ targetGiftCount, currentGiftCount, page, ...currentCounts });
      }
    }
    setIsOpen(!isOpen);
  };

  const getGiftCountsStateContent = () => {
    if (getResultsLoading) {
      return <LoadingResults />;
    } else if (isEditing || isInZeroState) {
      return (
        <SpacingStack width="100%" flex="1 1 100%">
          <EditGuestCount formik={formik} handleOnEditClick={handleOnEditClick} />
        </SpacingStack>
      );
    } else
      return (
        <SpacingStack width="100%" flex="1 1 100%">
          {isEditing || isInZeroState ? (
            <EditGuestCount formik={formik} handleOnEditClick={handleOnEditClick} />
          ) : (
            <CountsSummary
              completionCountTitle={completionCountTitle}
              completionPercent={completionPercent}
              formattedCompletionPercent={formattedCompletionPercent}
              handleOnEditClick={handleOnEditClick}
            />
          )}
        </SpacingStack>
      );
  };

  return (
    <SkeletonGroup width="100%" __css={styles.wrapper} justifyContent="center" isReady={!initialDataLoading} placeholder={<GiftCountsSkeleton />}>
      <StyledWrapper __css={styles.wrapper}>
        <StyledCardWrapper __css={styles.cardWrapper}>
          <StyledCardContent __css={styles.cardContent(isOpen)}>
            <Flex width="100%" alignItems={{ _: 'flex-start', sm2: 'center' }} flexDirection={{ _: 'column', sm2: 'row' }}>
              <Flex alignItems="center" flex="1 1 100%" marginBottom={{ _: 5, sm2: 0 }}>
                <Box marginRight={5}>
                  <GiftIcon />
                </Box>
                <Flex flexDirection="column">
                  <Flex alignItems="center">
                    <TextV2 typographyVariant="hed1" marginRight={2}>
                      {registryGuide()}
                    </TextV2>
                    <Tooltip />
                  </Flex>
                  {(isEditing || (isInZeroState && !getResultsLoading)) &&
                    (!!formik.errors.guestCount ? (
                      <TextV2 typographyVariant="body1" color="negative6">
                        {formik.errors.guestCount}
                      </TextV2>
                    ) : (
                      <TextV2 typographyVariant="body1">{howManyGuests()}</TextV2>
                    ))}
                </Flex>
              </Flex>
              {getGiftCountsStateContent()}
            </Flex>
            <ExpandButtonContainer flex="0 0 32px" marginLeft={{ _: 4, sm2: 6 }}>
              <StyledToggleButton as="button" __css={styles.expandButton(isOpen)} onClick={handleOnClick}>
                <Carrot />
              </StyledToggleButton>
            </ExpandButtonContainer>
          </StyledCardContent>
          <StyledDropdownContent __css={styles.dropdownContent(isOpen)}>
            <Flex
              flexDirection="column"
              padding={6}
              width="100%"
              alignItems="center"
              opacity={isEditing || getResultsLoading || isInZeroState ? 0.5 : 1}
              transition="opacity ease-in-out 300ms"
            >
              <TextV2 typographyVariant="hed2" marginBottom={6} textAlign="center">
                {countsByPrice()}
              </TextV2>
              <Flex justifyContent="space-evenly" flexWrap="wrap" width="100%">
                {(priceRanges || ZERO_STATE_PRICE_RANGES).map(priceRange => {
                  const { title, priceRangeQuery } = getPriceRangeTitleAndLink(priceRange);
                  return (
                    <PriceRange
                      page={page}
                      priceRangeQuery={priceRangeQuery}
                      isOpen={isOpen}
                      title={title}
                      key={title}
                      marginBottom={5}
                      flex={{ _: '1 1 50%', sm2: 'none' }}
                      targetGiftCount={priceRange.targetGiftCount}
                      completedGiftCount={priceRange.currentGiftCount}
                    />
                  );
                })}
              </Flex>
            </Flex>
          </StyledDropdownContent>
        </StyledCardWrapper>
        <StyledDivider __css={styles.divider} />
      </StyledWrapper>
    </SkeletonGroup>
  );
};
